import { reactive, readonly, DeepReadonly } from 'vue';
import { ICaptcha } from '@/domain/dto/captcha.interface';
import { injectScriptToBody } from '@studyportals/microservice-injector';
import { CaptchaType } from '@/domain/enums/captcha-type';

export interface ICaptchaInstance {
	readonly captcha: DeepReadonly<ICaptcha | null>;
	saveCaptcha(captcha: ICaptcha | null): void;
	loadCloudFlareCaptcha(): Promise<void>;
}

/* Singleton (see src/utils/globals.ts): one instance for the whole page, shared by globalStore and
	every incentiveV2Store, so the Turnstile script only ever loads once. Each caller keeps its own
	token isolated by calling createInstance() once and holding onto the returned handle. */
export class CaptchaService {
	private cloudFlareAwaitPromise: Promise<void> | null = null;
	private bypassCaptcha: ICaptcha | null = null;

	public createInstance(): ICaptchaInstance {
		const state = reactive<{ captcha: ICaptcha | null }>({ captcha: this.bypassCaptcha });

		return {
			get captcha(): DeepReadonly<ICaptcha | null> {
				return readonly(state).captcha;
			},
			saveCaptcha: (captcha: ICaptcha | null): void => {
				state.captcha = captcha;
			},
			loadCloudFlareCaptcha: (): Promise<void> => this.loadCloudFlareCaptcha(),
		};
	}

	public setBypassCaptcha(token: string): void {
		this.bypassCaptcha = {
			type: CaptchaType.BYPASS,
			token,
		};
	}

	private async loadCloudFlareCaptcha(): Promise<void> {
		if (this.cloudFlareAwaitPromise) {
			return this.cloudFlareAwaitPromise;
		}

		this.cloudFlareAwaitPromise = new Promise<void>((resolve) => {
			window['onloadTurnstileCallback'] = (): void => resolve();
			void injectScriptToBody('https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback');
		});

		return this.cloudFlareAwaitPromise;
	}
}
